/**
 * Where a failed OAuth connect sends the user, and in which window.
 *
 * Incident (filed 2026-08-11, root-caused 2026-08-12): both OAuth return pages
 * sent their "Try again" button to `/settings`. That page renders Profile /
 * Team / Danger zone and holds no connect control at all; every connect card
 * for all seven providers lives on `/integrations` (`pages/IntegrationsPage`).
 * The sidebar labels `/settings` "Account", so the destination did not even
 * carry the name the user was sent to. It hit every user whose authorization
 * failed, at the moment it failed.
 *
 * The path was only half the defect. Both pages render INSIDE the 600x700 popup
 * opened by `useFigmaToken` / `useIntegrations.openOAuthPopup`, and the opener
 * settles its pending mutation only when that popup CLOSES — it polls
 * `popup.closed` under a 5-minute ceiling. An in-popup navigation to any
 * dashboard route therefore strands that poll and renders the full dashboard in
 * a 600x700 window. Swapping the literal alone would have fixed what the user
 * could see and left that intact. It is the same failure the 2026-06-22
 * investigation found on the server redirect (see
 * `packages/server/src/services/oauth-providers/base.ts:80-84`): landing on
 * `/settings` left the popup open forever and the card stayed "Connect".
 *
 * So the retry drives the OPENER, which already holds the connect cards and the
 * pending mutation, and then closes the popup so the opener settles at once.
 *
 * In-place navigation is the fallback, not the default, and it has two real
 * callers: the extension opens this page with `chrome.windows.create({type:
 * 'popup'})`, which has no opener at all, and a provider serving its consent
 * screen under COOP can sever the opener mid-flow. Both surfaces resolve to the
 * same account-level connection, so `/integrations` is a correct retry for
 * either entry point.
 *
 * Injected-deps module (house pattern: `openCreatedReview.ts`) so tests never
 * touch window.open / window.location.
 */

/** The only dashboard route that renders connect cards. */
export const OAUTH_RETRY_PATH = '/integrations';

export type RetryOAuthConnectOutcome = 'opener' | 'in-place';

export interface RetryOAuthConnectDeps {
  getOpener?: () => Window | null;
  navigateOpener?: (opener: Window, url: string) => void;
  navigateSelf?: (url: string) => void;
  closeSelf?: () => void;
}

const defaultGetOpener = (): Window | null => window.opener as Window | null;
const defaultNavigateOpener = (opener: Window, url: string): void => opener.location.assign(url);
const defaultNavigateSelf = (url: string): void => window.location.assign(url);
const defaultCloseSelf = (): void => window.close();

export function retryOAuthConnect(deps: RetryOAuthConnectDeps = {}): RetryOAuthConnectOutcome {
  const getOpener = deps.getOpener ?? defaultGetOpener;
  const navigateOpener = deps.navigateOpener ?? defaultNavigateOpener;
  const navigateSelf = deps.navigateSelf ?? defaultNavigateSelf;
  const closeSelf = deps.closeSelf ?? defaultCloseSelf;

  let opener: Window | null = null;
  try {
    opener = getOpener();
    // A closed opener cannot be navigated: the assignment is a silent no-op, so
    // closing this popup afterwards would leave the user with nothing on screen.
    if (opener?.closed) opener = null;
  } catch {
    // A COOP-severed opener throws on access. Treat it as absent.
    opener = null;
  }

  if (opener) {
    try {
      navigateOpener(opener, OAUTH_RETRY_PATH);
    } catch {
      // The opener refused the navigation, so this popup is still the user's
      // only surface. Fall through and navigate it rather than closing it.
      navigateSelf(OAUTH_RETRY_PATH);
      return 'in-place';
    }

    try {
      closeSelf();
    } catch {
      // The retry already landed where the user needs it. A popup we cannot
      // close is cosmetic; re-navigating it would undo nothing useful.
    }
    return 'opener';
  }

  navigateSelf(OAUTH_RETRY_PATH);
  return 'in-place';
}
